import { useToken } from "@gluestack-style/react";
import DateSelect from "components/general/DateSelect";
import { Box, Button, Checkbox, CheckboxIcon, CheckboxIndicator, HStack, Icon, Input, InputField, ParagraphSmall, Radio, RadioGroup, RadioIndicator, RadioLabel, Select, Text, Typography, VStack } from "components/ui";
import { FormikProps } from "formik";
import { AlertCircle, CheckIcon, CircleIcon, CirclePlus, Eye, EyeOff } from "lucide-react-native";
import React, { useState } from "react";

// Generic form values interface that can accept any key-value pairs
interface GenericFormValues {
	[key: string]: any;
}

interface FormFieldProps<T extends GenericFormValues = GenericFormValues> {
	label: string;
	name: string; // Changed from keyof T to string to support nested fields
	placeholder: string;
	type?: "input" | "select" | "date" | "radio" | "image" | "checkbox";
	options?: Array<{ value: string; label: string }>;
	formik: FormikProps<T>;
	helperText?: string;
	imageFieldName?: string; // Changed from keyof T to string
	isPassword?: boolean;
	onChange?: (event: any) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	withLabel?: boolean;
	autoFocus?: boolean;
	onSubmitEditing?: () => void;
}

interface ImageUploadProps<T extends GenericFormValues = GenericFormValues> {
	formik: FormikProps<T>;
	fieldName: string;
}

const ImageUpload = <T extends GenericFormValues = GenericFormValues>({ formik, fieldName }: ImageUploadProps<T>) => {
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const uploadId = `image-upload-${String(fieldName)}`;

	const handleImageDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const file = event.dataTransfer.files[0];
		if (file) {
			formik.setFieldValue(fieldName as string, file);
		}
	};

	const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			formik.setFieldValue(fieldName as string, file);
		}
	};

	return (
		<Box
			sx={{
				borderRadius: 8,
				padding: 20,
				alignItems: "center",
				justifyContent: "center",
				minHeight: 200,
				backgroundColor: "$basicwhite",
				borderStyle: "dashed",
				borderWidth: 2,
				borderColor: "$primarylight",
			}}
			// onDragOver={(e:any) => e.preventDefault()}
			// onDrop={handleImageDrop}
		>
			<input type="file" accept=".png, .jpg, .gif" onChange={handleImageSelect} style={{ display: "none" }} id={uploadId} />
			<Box>
				<Box alignItems="center" py={12}>
					<Button padding={0} onPress={() => document.getElementById(uploadId)?.click()}>
						<CirclePlus size={48} color={resolvedPrimarylight} />
					</Button>
				</Box>
				<HStack gap={6}>
					<Typography weight={"bold"}>Drag and Drop</Typography>
					<Typography>or</Typography>
					<Button padding={0} onPress={() => document.getElementById(uploadId)?.click()}>
						<Typography sx={{ color: "$primarylight" }} weight={"bold"}>
							Choose a file
						</Typography>
					</Button>
					<Typography>to upload.</Typography>
				</HStack>
				<HStack alignItems="center" justifyContent="center" gap={6}>
					<Typography>Supported formats:</Typography>
					<Typography weight={"bold"}>PNG , JPG, GIF</Typography>
				</HStack>
			</Box>
			{formik.values[fieldName as string] && <Text mt={10}>{(formik.values[fieldName as string] as File).name}</Text>}
		</Box>
	);
};

const FormField = <T extends GenericFormValues = GenericFormValues>({
	label,
	withLabel = true,
	name,
	placeholder,
	type = "input",
	options = [],
	formik,
	helperText,
	imageFieldName,
	isPassword = false,
	onChange,
	onFocus,
	onBlur,
	autoFocus,
	onSubmitEditing,
}: FormFieldProps<T>) => {
	const resolvedErrorBright = useToken("colors", "errorbright");
	const error = formik.errors[name];
	const touched = formik.touched[name];
	const fieldName = imageFieldName || name;
	const [showPassword, setShowPassword] = useState(false);

	const handleChange = (value: any) => {
		if (onChange) {
			onChange({ target: { value } });
		}
		formik.setFieldValue(name, value, true);
	};

	const handleFocus = () => {
		onFocus?.();
	};

	const handleBlur = () => {
		onBlur?.();
		formik.setFieldTouched(name, true);
	};

	// Helper function to get nested value
	const getNestedValue = (obj: any, path: string) => {
		const result = path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);
		return result ?? "";
	};

	const fieldValue = getNestedValue(formik.values, name);

	return (
		<Box>
			<Box gap={12}>
				{withLabel && <Text sx={{ fontFamily: "$robotoBold", fontSize: 14 }}>{label || ""}</Text>}

				{type === "input" && (
					<Box position="relative">
						<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
							<InputField
								onChangeText={handleChange}
								onFocus={handleFocus}
								onBlur={handleBlur}
								value={String(fieldValue)}
								placeholder={placeholder || ""}
								secureTextEntry={isPassword && !showPassword}
								autoFocus={autoFocus}
								onSubmitEditing={onSubmitEditing}
								returnKeyType="done"
								aria-label={label || ""}
							/>
						</Input>

						{isPassword && (
							<Button position="absolute" right={10} top="50%" transform="translateY(-50%)" onPress={() => setShowPassword(!showPassword)}>
								{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
							</Button>
						)}
					</Box>
				)}

				{type === "select" && <Select options={options} value={options.find((option) => option.value === fieldValue)} onChange={(value) => formik.setFieldValue(name, value)} placeholder={placeholder} />}

				{type === "date" && <DateSelect onDateChange={(value) => formik.setFieldValue(name, value)} />}

				{type === "radio" && (
					<RadioGroup value={String(fieldValue)} onChange={(value) => formik.setFieldValue(name, value)}>
						<VStack py={16} gap={16}>
							{options.map((option) => (
								<Radio key={option.value} value={option.value}>
									<RadioIndicator mr="$2">{fieldValue === option.value && <Icon fill="$primarylight" as={CircleIcon} />}</RadioIndicator>
									<RadioLabel>
										<Text>{option.label || ""}</Text>
									</RadioLabel>
								</Radio>
							))}
						</VStack>
					</RadioGroup>
				)}

				{type === "checkbox" && (
					<Checkbox
						value={fieldValue ? "true" : "false"}
						onChange={(isSelected) => {
							formik.setFieldValue(name, isSelected);
							if (onChange) {
								onChange({ target: { value: isSelected } });
							}
						}}
						aria-label={placeholder || ""}
						style={{ marginBottom: 12, alignItems: "center", justifyContent: "center" }}
					>
						<CheckboxIndicator>
							<CheckboxIcon as={CheckIcon} />
						</CheckboxIndicator>
						<Typography weight="bold">{placeholder || ""}</Typography>
					</Checkbox>
				)}

				{type === "image" && <ImageUpload formik={formik} fieldName={fieldName} />}

				{helperText && (
					<Box>
						<ParagraphSmall sx={{ color: "$neutral300" }}>{helperText}</ParagraphSmall>
					</Box>
				)}

				{error && touched && (
					<HStack gap={6} py={6}>
						<AlertCircle color={resolvedErrorBright} />
						{typeof error === "string" && error.trim() && <Typography.ParagraphSmall sx={{ color: "$errorbright" }}>{error}</Typography.ParagraphSmall>}
					</HStack>
				)}
			</Box>
		</Box>
	);
};

export default FormField;
